import { Box, Button, Col, Tooltip } from '@cubeyou-team/hx-react-components';
import classNames from 'classnames';
import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { TRIAL_FEATURES, useTrialPaywall } from 'src/modules/trial-paywall';
import {
  changeQueryBuilderTypeRequest,
  sendEvent,
  showCreateAudienceModal,
  showCreateTsvModal,
} from '../../../actions';
import { RepositoryConstants } from '../../../constants/repository';
import { isAvailableQueryBuilderType } from '../../../utils/AuthHelperMethods';

const StyledButton = styled(Button)<{ $compact: boolean }>`
  ${(props) => !props.$compact && 'padding: 0 24px;'}
  ${(props) => props.$compact && 'height: 40px;'}

  & > i {
    padding: 0;
    & > * {
      height: ${(props) => (props.$compact ? '1.25rem' : '1.375rem')};
      width: ${(props) => (props.$compact ? '1.25rem' : '1.375rem')};
    }
  }
`;

interface NewButtonProps {
  compact?: boolean;
}

export const RepositoryActions: FC<NewButtonProps> = ({ compact = false }) => {
  const dispatch = useDispatch();
  const layout = useSelector((state: any) => state.layout);
  const { guardFeature } = useTrialPaywall();

  const handleInterestAudience = useCallback(() => {
    dispatch(showCreateAudienceModal());
    dispatch(changeQueryBuilderTypeRequest('default'));
    dispatch(sendEvent('Analyze/Query_Builder_Interests_v2'));
  }, [dispatch]);

  const handleConversationAudience = useCallback(() => {
    guardFeature(TRIAL_FEATURES.CONVERSATION, () => {
      dispatch(showCreateAudienceModal());
      dispatch(changeQueryBuilderTypeRequest('conversation'));
      dispatch(sendEvent('Analyze/Query_Builder_Conversation_v2'));
    });
  }, [dispatch, guardFeature]);

  const handleTotalSocialView = useCallback(() => {
    guardFeature(TRIAL_FEATURES.TOTAL_SOCIAL_VIEW, () => {
      dispatch(showCreateTsvModal());
      dispatch(sendEvent('Workspace'));
    });
  }, [dispatch, guardFeature]);

  const buttonSize = useMemo(
    () => (layout.repositorySize === 'full' ? 'medium' : 'small'),
    [layout.repositorySize]
  );

  const buttons = useMemo(() => {
    return [
      {
        size: layout.repositorySize === 'collapsed' ? 'small' : 'medium',
        variant: 'secondary',
        icon: 'heart_plus',
        label: 'Interest audiences',
        description: 'Interest audiences',
        onClick: handleInterestAudience,
        condition: isAvailableQueryBuilderType(RepositoryConstants.INTEREST),
      },
      {
        size: buttonSize,
        variant: compact ? 'secondary' : 'primary',
        icon: 'conversation_plus',
        label: 'Conversation',
        description: 'Conversation monitor',
        onClick: handleConversationAudience,
        condition: isAvailableQueryBuilderType(
          RepositoryConstants.CONVERSATION
        ),
      },
      {
        size: buttonSize,
        variant: compact ? 'secondary' : 'primary',
        icon: 'users_plus',
        label: 'Total Social View',
        description: 'Total Social View Upload',
        onClick: handleTotalSocialView,
        condition: isAvailableQueryBuilderType(RepositoryConstants.TSV),
      },
    ];
  }, [
    compact,
    buttonSize,
    layout.repositorySize,
    handleInterestAudience,
    handleConversationAudience,
    handleTotalSocialView,
  ]);

  const ButtonComponent = ({ action }) => (
    <StyledButton
      onClick={action.onClick}
      size={action.size}
      variant={action.variant}
      icon={action.icon}
      $compact={compact}
      className={classNames('flex', {
        'justify-center w-10 h-10 mt-2': compact,
        'justify-start w-full mt-0': !compact,
      })}
      style={compact ? { paddingRight: '8px' } : undefined}
    >
      {compact ? (
        <p className="hidden">{action.icon}</p>
      ) : (
        <span>{action.label}</span>
      )}
    </StyledButton>
  );

  return (
    <>
      <div
        className={classNames('m-4', {
          'ml-2': layout.repositorySize !== 'collapsed',
        })}
      >
        <div
          className={classNames('flex gap-2', {
            'flex-col': layout.repositorySize !== 'full',
            'flex-row': layout.repositorySize === 'full',
          })}
        >
          {buttons
            .filter((action) => action.condition)
            .map((action, index) => (
              <Col
                key={`new-section-${index}`}
                span={layout.repositorySize === 'full' ? 'auto' : 12}
              >
                {compact ? (
                  <Tooltip
                    content={action.description}
                    position="right"
                    full
                    maxWidth="medium"
                  >
                    <ButtonComponent action={action} />
                  </Tooltip>
                ) : (
                  <ButtonComponent action={action} />
                )}
              </Col>
            ))}
        </div>
      </div>
      <Box borderSize="1px" borderPosition="bottom" />
    </>
  );
};
